
import React, { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { InputModeMap, InputTypeMap } from './FormInputTypes';

interface FormInputCoreProps extends React.InputHTMLAttributes<HTMLInputElement> {
  name: string;
  label?: string;
  description?: string;
  error?: string;
  fullWidth?: boolean;
  containerClassName?: string;
  labelClassName?: string;
  isRequired?: boolean;
  inputType?: 'text' | 'numeric' | 'alphabets' | 'decimal' | 'tel' | 'email' | 'url';
  onInputChange?: (value: string) => void;
}

export function FormInputCore({
  name,
  label,
  description,
  error,
  fullWidth = true,
  containerClassName,
  labelClassName,
  isRequired = false,
  inputType = 'text',
  className,
  value,
  onChange,
  onBlur,
  onFocus,
  onInputChange,
  placeholder,
  ...props
}: FormInputCoreProps) {
  const [isFocused, setIsFocused] = useState(false);
  const [isTouched, setIsTouched] = useState(false);
  
  // Create an ID for accessibility
  const inputId = `form-input-${name}`;

  // Handle input change with appropriate validation based on inputType
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    
    // Apply validation based on inputType
    let isValid = true;
    
    if (value !== '') {
      switch (inputType) {
        case 'alphabets':
          isValid = /^[a-zA-Z\s\u00C0-\u017F]*$/.test(value); // Improved regex for alphabets including accented chars
          break;
        case 'numeric':
          isValid = /^[0-9]*$/.test(value);
          break;
        case 'decimal':
          isValid = /^[0-9]*\.?[0-9]*$/.test(value);
          break;
        case 'tel':
          isValid = /^[0-9+\-\s()]*$/.test(value); // Allow common phone number characters
          break;
        case 'email':
          // We'll let HTML5 validation handle this, just ensure no spaces
          isValid = !/\s/.test(value);
          break;
        default:
          // No validation for other types
          break;
      }
    }
    
    // Only proceed if the input is valid or empty
    if (isValid) {
      if (onChange) {
        onChange(e);
      }
      
      if (onInputChange) {
        onInputChange(e.target.value);
      }
    }
  };

  return (
    <div className={cn(
      'space-y-2',
      fullWidth ? 'w-full' : '',
      containerClassName
    )}>
      {label && (
        <Label 
          htmlFor={inputId}
          className={cn(
            isRequired ? 'after:content-["*"] after:ml-0.5 after:text-destructive' : '',
            labelClassName
          )}
        >
          {label}
        </Label>
      )}
      <Input
        id={inputId}
        name={name}
        inputMode={InputModeMap[inputType]}
        type={InputTypeMap[inputType] || props.type || 'text'}
        className={cn(
          error ? 'border-destructive focus:border-destructive' : '',
          className
        )}
        value={value}
        placeholder={placeholder || `Enter ${name}`}
        {...props}
        onFocus={(e) => {
          setIsFocused(true);
          onFocus?.(e);
        }}
        onBlur={(e) => {
          setIsFocused(false);
          setIsTouched(true);
          onBlur?.(e);
        }}
        onChange={handleInputChange}
      />
      {description && <p className="text-sm text-muted-foreground">{description}</p>}
      {error && (
        <p className="text-sm font-medium text-destructive">{error}</p>
      )}
    </div>
  );
}
